import { useRefImageSource } from '../lib/refImageSource';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { clientApi, ApiError } from '../lib/api';
import type { PublicScript } from '../../shared/types';
import {
  extractInterviewQuestions,
  extractShootFields,
} from '../../shared/extractInterviewQuestions';
import { isBriefTemplate } from '../../shared/briefFields';
import { extractScriptRows, shootFamily } from '../../shared/scriptRows';
import Wordmark from '../components/Wordmark';
import InterviewQuestionCard from '../components/InterviewQuestionCard';
import Button from '../components/Button';
import ContactLine from '../components/ContactLine';
import Eyebrow from '../components/Eyebrow';
import Toaster, { showToast } from '../components/Toaster';
import ShootTabs from '../components/Shoot/ShootTabs';
import ShootBriefTab from '../components/Shoot/ShootBriefTab';
import ShootScriptTab from '../components/Shoot/ShootScriptTab';
import { shootableRows } from '../components/Shoot/useScriptFilter';
import { useShootTab, type ShootTab } from '../components/Shoot/useShootTab';
import { useSwipeTabs } from '../components/Shoot/useSwipeTabs';
import { downloadBriefPdf } from '../lib/downloadPdf';
import { useForceTheme } from '../lib/theme';
import { useBundleInterview } from '../lib/bundleInterviewContext';
import { useCallsheetEmbed } from '../lib/callsheetEmbed';

/**
 * Public-facing shoot-day reference page. Same data shape as the admin
 * Shoot view but pulls the doc through the unauthenticated /scripts/:slug
 * endpoint, so anyone with the URL — Henry or the on-set client team —
 * can pull up just the interview questions on a phone in big readable
 * text. The standard /s/:slug review URL gets a button that opens this
 * route in a new tab.
 *
 * Also mounted (via BundleScriptView) at /b/:bundleSlug/:slug for shoot-kind
 * bundles — see docs/script-bundles-plan.md §5 "Shoot pack". BundleInterviewContext
 * is present only in that case; its mere presence forces the brand's dark
 * exception instead of the standalone route's light theme, and swaps the
 * header's back-link + label for the bundle-appropriate read-only framing.
 * Standalone /s/:slug/interview renders exactly as before — this is the only
 * conditional in the file.
 *
 * Scripts (every template but the interview brief) open in Script mode: the
 * rows as reading lines with To camera / VO filters. A script that also has a
 * legacy "Interview questions" section gets Script and Questions tabs; one
 * with only the questions renders as it always has.
 *
 * `?embed=callsheet` (see lib/callsheetEmbed.ts) drops the header so the call
 * sheet's own bar can sit above the frame. Presentation only.
 */
export default function ClientInterviewView() {
  const bundleContext = useBundleInterview();
  const embedded = useCallsheetEmbed();
  useForceTheme(bundleContext ? 'dark' : 'light');
  const { slug } = useParams<{ slug: string }>();
  useRefImageSource(slug ? { kind: 'client', slug } : null);
  const [script, setScript] = useState<PublicScript | null>(null);
  const [error, setError] = useState<'not_found' | 'revising' | 'error' | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // ---- Tabs (briefs, and scripts that also carry questions) ----
  // Computed above the early returns below, so the doc is read through an
  // empty string until the script lands.
  const headerRef = useRef<HTMLElement>(null);
  const docJson = script?.doc_json ?? '';
  const isScript = script ? shootFamily(script.template_key) === 'script' : false;
  const questions = useMemo(() => extractInterviewQuestions(docJson), [docJson]);
  const shootFields = useMemo(() => extractShootFields(docJson), [docJson]);
  const rows = useMemo(
    () => (isScript ? shootableRows(extractScriptRows(docJson)) : []),
    [docJson, isScript],
  );
  const hasRows = rows.length > 0;
  const hasQuestions = questions !== null && questions.length > 0;
  // The left-hand tab: a brief's chosen fields, or a script's rows when the
  // script has questions too. Rows alone need no tabs.
  const lead = isScript ? 'script' : 'brief';
  const hasFields = isScript ? hasRows && hasQuestions : shootFields.length > 0;
  const scriptOnly = hasRows && !hasQuestions;
  const [tab, setTab] = useShootTab(slug ?? '', hasFields, hasQuestions, lead);
  const changeTab = useCallback((next: ShootTab) => {
    setTab(next);
    window.scrollTo({ top: 0 });
  }, [setTab]);
  const swipe = useSwipeTabs(tab, changeTab, lead);
  const panel: ShootTab = scriptOnly ? 'script' : tab;
  // Inside a bundle nothing above the strips sticks; in the call-sheet frame
  // the header is gone, so they park at the very top.
  const stickyUnder = bundleContext || embedded ? undefined : headerRef;

  const load = useCallback(() => {
    if (!slug) return;
    clientApi
      .getScript(slug)
      .then(setScript)
      .catch((err) => {
        if (err instanceof ApiError && (err.status === 404 || err.status === 410)) {
          setError('not_found');
        } else if (err instanceof ApiError && err.status === 423) {
          setError('revising');
        } else {
          setError('error');
        }
      });
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  // The client copy, exactly as the review page's Download PDF builds it: the
  // doc has already been projected by the worker and the renderer projects
  // again on the way in, so an internal field cannot reach this file.
  async function handleDownloadPdf() {
    if (!script || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      await downloadBriefPdf({
        title: script.title,
        client_name: script.client_name,
        status: script.status,
        approved_at: script.approved_at,
        slug: script.slug,
        doc_json: script.doc_json,
        variant: 'client',
      });
    } catch {
      showToast('error', 'That PDF didn’t generate. Please try again in a moment.');
    } finally {
      setDownloadingPdf(false);
    }
  }

  if (error) {
    // Keep script headings: script is null here, so its brief/script kind is unknown.
    const copy =
      error === 'not_found'
        ? {
            eyebrow: '// SHOOT VIEW',
            heading: 'Script not found',
            body: 'This link may be out of date. Henry can send you a fresh one.',
          }
        : error === 'revising'
        ? {
            eyebrow: '// BEING REVISED',
            heading: 'This script is being revised',
            body: 'Henry is working on the next version. It will be back shortly.',
          }
        : {
            eyebrow: '// SOMETHING WENT WRONG',
            heading: 'We couldn’t load the questions',
            body: 'It is most likely temporary. Worth another try, especially on shoot-day wifi.',
          };
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Eyebrow accent>{copy.eyebrow}</Eyebrow>
          <h1 className="font-display font-bold text-[32px] leading-[1.1] tracking-[-0.02em] mt-3 mb-3">
            {copy.heading}
          </h1>
          <p className="text-muted leading-[1.55]">{copy.body}</p>
          {error === 'error' && (
            <div className="mt-6">
              <Button
                variant="primary"
                glow
                onClick={() => {
                  setError(null);
                  load();
                }}
              >
                Try again
              </Button>
            </div>
          )}
          {/* The script is null on this screen, so the way back is built from
              the route param. Inside a pack the switcher bar is the way out. */}
          {error === 'revising' && !bundleContext && slug && (
            <Link
              to={`/s/${slug}`}
              className="text-[13px] text-accent hover:underline mt-4 inline-block"
            >
              ← Back to the review page
            </Link>
          )}
          <ContactLine className="mt-6" />
        </div>
      </main>
    );
  }

  if (!script) {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center">
        <p className="text-faint font-mono text-[12px] uppercase tracking-[0.12em]">Loading…</p>
      </main>
    );
  }

  const isBrief = isBriefTemplate(script.template_key);

  return (
    <main className="min-h-screen bg-page text-fg">
      {/* Non-sticky when embedded in a bundle: the dark switcher bar above
          us (BundleScriptView) already sticks at top-0 for "which script/back
          to all" — a second stuck header at the same offset would fight it
          for the top of the viewport. Standalone stays sticky as before. */}
      {!embedded && (
      <header
        ref={headerRef}
        className={`border-b border-line bg-page/90 backdrop-blur ${
          bundleContext ? '' : 'sticky top-0 z-20'
        }`}
      >
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
          {bundleContext ? (
            <Wordmark size="sm" />
          ) : (
            <Link
              to={`/s/${script.slug}`}
              className="flex items-center gap-2 text-muted hover:text-fg transition-colors"
            >
              <span aria-hidden>←</span>
              <Wordmark size="sm" />
            </Link>
          )}
          <div className="flex items-center gap-3">
            {/* The PDF button now shares this row, so the label holds one
                line and a pack drops its "read-only" tail on a phone, where
                the switcher bar above already says the page is a pack. */}
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint whitespace-nowrap">
              Shoot view
              {bundleContext && <span className="hidden sm:inline"> · read-only</span>}
            </span>
            {/* Briefs only, packs included: a pack has no review page to go
                and fetch the PDF from. Scripts keep no PDF button here. */}
            {isBrief && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                title="Download the brief as a PDF"
              >
                {downloadingPdf ? 'Preparing…' : 'PDF'}
              </Button>
            )}
          </div>
        </div>
      </header>
      )}

      <section className="max-w-2xl mx-auto px-5 py-8 md:py-12">
        <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-faint mb-2">
          // {script.client_name}
        </p>
        <h1 className="font-display font-bold text-[28px] md:text-[36px] leading-[1.1] tracking-[-0.02em] mb-2">
          {script.title}
        </h1>
        <p
          className={`text-muted text-[14px] ${
            hasFields || scriptOnly ? 'mb-3' : 'mb-10'
          }`}
        >
          {isScript && (hasRows || !hasQuestions)
            ? 'Script'
            : hasFields
              ? 'Interview brief'
              : 'Interview questions'}
        </p>

        {(hasFields || scriptOnly) && (
          <>
            {hasFields && (
              <ShootTabs
                active={tab}
                onChange={changeTab}
                lead={lead}
                leadCount={isScript ? rows.length : shootFields.length}
                questionCount={questions?.length ?? 0}
                stickyUnder={stickyUnder}
                stickyAtTop={embedded}
              />
            )}
            {/* Stripped of comments and the approve button, the page could
                read as a broken brief. One line says what it is instead. */}
            <p className={`text-[13px] text-muted leading-snug ${hasFields ? 'mt-3' : 'mb-2'}`}>
              {isScript
                ? 'Every line and shot, sized for a phone.'
                : 'The messaging and the questions, sized for a phone.'}
              {!bundleContext && !embedded && ' To comment or approve, go back to the review page.'}
            </p>
          </>
        )}

        <div
          className={hasFields ? 'mt-5' : undefined}
          onTouchStart={hasFields ? swipe.onTouchStart : undefined}
          onTouchEnd={hasFields ? swipe.onTouchEnd : undefined}
        >
          {isScript ? (
            <div
              id="shoot-panel-script"
              role={hasFields ? 'tabpanel' : undefined}
              aria-labelledby={hasFields ? 'shoot-tab-script' : undefined}
              hidden={panel !== 'script'}
            >
              {hasRows && (
                <ShootScriptTab
                  slug={script.slug}
                  rows={rows}
                  stickyUnder={scriptOnly ? stickyUnder : undefined}
                  stickyAtTop={scriptOnly && embedded}
                />
              )}
            </div>
          ) : (
            <div
              id="shoot-panel-brief"
              role="tabpanel"
              aria-labelledby="shoot-tab-brief"
              hidden={tab !== 'brief'}
            >
              {hasFields && <ShootBriefTab docJson={script.doc_json} />}
            </div>
          )}
          <div
            id="shoot-panel-questions"
            role={hasFields ? 'tabpanel' : undefined}
            aria-labelledby={hasFields ? 'shoot-tab-questions' : undefined}
            hidden={panel !== 'questions'}
          >
            {!questions || questions.length === 0 ? (
              <div className="border border-dashed border-line rounded-md px-6 py-12 text-center bg-card">
                <p className="text-muted">
                  {isBrief
                    ? 'No interview questions in this brief yet.'
                    : 'Nothing to show for this script yet.'}
                </p>
                {!isBrief && (
                  <p className="text-faint text-[13px] mt-2">
                    Its lines will appear here once the script has some.
                  </p>
                )}
              </div>
            ) : (
              <ol className="space-y-5">
                {questions.map((q, i) => (
                  <InterviewQuestionCard key={i} index={i} question={q} />
                ))}
              </ol>
            )}

            <p className="text-faint text-[12px] mt-12 text-center">
              {questions?.length ?? 0} question{questions?.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
      </section>
      <Toaster />
    </main>
  );
}
